import type { ComponentType, ReactNode } from 'react';
import { cn } from '../cn.js';

export interface EmptyStateProps {
  /** An icon from `@ccp/ui/icons`, drawn muted. Decorative: the title carries the meaning. */
  readonly icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>;
  readonly title: string;
  /** Why it is empty, in one sentence. Not an apology. */
  readonly description?: string;
  /** The next thing to do. An empty state with nothing to do is a dead end. */
  readonly action?: ReactNode;
  readonly className?: string;
}

/**
 * Nothing here, and what to do about it.
 *
 * **Every list and panel has one**, by the design system's own standard, and eleven screens in
 * this repository had invented their own sentence instead. The difference between a good empty
 * state and a bad one is not the drawing: it is whether it says *why* the list is empty and what
 * the person can do next. "No tenants yet" with a "New tenant" button is a product; "No data" is
 * a shrug.
 *
 * It is not an error. An empty list is a normal state and must not be announced as a failure —
 * that is `ErrorState`, which has a `role="alert"` and this deliberately does not.
 */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-(--space-sm)',
        'rounded-(--radius-md) border border-dashed border-(--color-border)',
        'px-(--space-lg) py-(--space-xl) text-center',
        className,
      )}
    >
      {Icon === undefined ? null : (
        <Icon aria-hidden="true" className="size-(--size-icon-lg) text-(--color-text-muted)" />
      )}
      <p className="text-(length:--font-size-body-large) font-medium text-(--color-text-primary)">
        {title}
      </p>
      {description === undefined ? null : (
        <p className="max-w-(--width-container-narrow) text-(length:--font-size-body) text-(--color-text-secondary)">
          {description}
        </p>
      )}
      {action === undefined ? null : <div className="mt-(--space-xs)">{action}</div>}
    </div>
  );
}
